// Column layout maths for the per-user column picker (issue #148).
// Kept free of React so the ordering rules stay easy to reason about.
//
// Locked columns hold their place because nothing can be dropped onto them,
// not because they are re-pinned after the fact: `move` and `step` refuse a
// locked source or target, so the first column stays first and Actions stays
// last on its own. That keeps a drag doing exactly what it looks like it does.
// Timesheets is why this matters: its Status column is locked but sits mid
// table, and an earlier rule that pinned locked columns to a fixed index made
// unrelated columns jump across Status whenever something moved.

// Applies the user's key order to the catalogue. Columns the order never
// mentions keep their default slot, so a column added later is not stranded.
export const orderColumns = (catalogue, order) => {
  const byKey = new Map(catalogue.map(col => [col.key, col]));
  const placed = new Set();
  const result = [];

  order.forEach(key => {
    const col = byKey.get(key);
    if (col && !placed.has(key)) {
      placed.add(key);
      result.push(col);
    }
  });

  catalogue.forEach((col, index) => {
    if (placed.has(col.key)) return;
    result.splice(Math.min(index, result.length), 0, col);
  });

  return result;
};

// Rebuilds the key order from what the server sent. Saved keys the catalogue no
// longer has are dropped; catalogue columns the save never mentioned (added
// since the user last saved) slot back in at their default index, which is what
// keeps a new column from being invisible to everyone who already has a layout.
export const mergeSavedOrder = (catalogue, saved) => {
  const known = new Set(catalogue.map(col => col.key));
  const savedVisible = (saved?.visible ?? []).filter(key => known.has(key));
  const savedHidden = (saved?.hidden ?? []).filter(key => known.has(key));
  const mentioned = new Set([...savedVisible, ...savedHidden]);

  const order = [...savedVisible];
  catalogue.forEach((col, index) => {
    if (mentioned.has(col.key)) return;
    order.splice(Math.min(index, order.length), 0, col.key);
  });
  // hidden columns keep a slot in the order so the picker lists them somewhere
  // sensible; the hidden set is what removes them from the table
  savedHidden.forEach(key => {
    if (order.includes(key)) return;
    const index = catalogue.findIndex(col => col.key === key);
    order.splice(Math.min(index, order.length), 0, key);
  });
  return order;
};

// Locked columns can never be hidden, whatever a stale save says.
export const mergeSavedHidden = (catalogue, saved) => {
  if (!saved) return defaultHidden(catalogue);
  const lockable = new Set(catalogue.filter(col => !col.locked).map(col => col.key));
  return new Set((saved.hidden ?? []).filter(key => lockable.has(key)));
};

export const defaultOrder = (catalogue) => catalogue.map(col => col.key);

export const defaultHidden = (catalogue) =>
  new Set(catalogue.filter(col => col.defaultHidden).map(col => col.key));

// Moves `key` to sit where `targetKey` currently sits, shifting the rest along.
// Callers reject locked columns before getting here.
export const moveKey = (order, key, targetKey) => {
  if (key === targetKey) return order;
  const next = order.filter(k => k !== key);
  const target = next.indexOf(targetKey);
  if (target === -1) return order;
  next.splice(target, 0, key);
  return next;
};

// Steps `key` one place through the columns it can actually swap with, so the
// move buttons reach the same positions a drag does.
export const stepKey = (catalogue, order, key, direction) => {
  const lockedKeys = new Set(catalogue.filter(col => col.locked).map(col => col.key));
  const movable = order.filter(k => !lockedKeys.has(k));
  const from = movable.indexOf(key);
  const to = from + direction;
  if (from === -1 || to < 0 || to >= movable.length) return order;
  return moveKey(order, key, movable[to]);
};
